import React, { memo, useCallback, useMemo, useState } from 'react';
import TableBody from '@mui/material/TableBody';
import Table from '@mui/material/Table';
import Paper from '@mui/material/Paper';
import TableContainer from 'src/components/Table/TableContainer';
import TableHeading from 'src/components/Table/TableHeading';
import LoadingRows from 'src/components/Table/LoadingRows';
import Row from 'src/components/Table/Row';
import NoDataCell from 'src/components/NoDataCell';
import { scriptExecutionTableColumnsOrder, scriptExecutionTableHeadings } from './constants';
import { Box, Stack, TableCell } from '@mui/material';
import TableSearch from 'src/components/elements/TableSearch';
import useQueryParams from 'src/hooks/useQueryParams';
import useDebounceHandler from 'src/hooks/useDebounceHandler';
import {
  defaultSearchParams,
  reversedSortMap,
  ROW_SOURCES,
  sortingMethods,
} from 'src/constants/table';
import useResetSearchParams from 'src/hooks/useResetSearchParams';
import FilterCount from 'src/components/Filters/FilterCount';
import { checkHasFilters } from 'src/utils/table.util';
import GenericTableCell from 'src/components/GenericTableCell';
import { isDateString } from 'src/utils/date.util';
import { formatDate } from 'src/utils/formatTime';
import useTableSort from '../../../../hooks/useTableSort';
import { defaultScriptExecutionFilters } from '../../../../components/Filters/constants';
import WdacScriptExecutionSidebar from './WdacScriptExecutionSidebar';
import {
  scriptExecutionDeviceTableColumnsOrder,
  scriptExecutionDeviceTableHeadings,
} from '../../../devices/deviceDetails/scriptExecution/constants';

const WdacScriptExecutionTable = ({
  isLoading,
  scriptExecutionList,
  lastElementRef,
  isFetching,
  totalCount,
  query,
  type,
}) => {
  const [searchValue, setSearchValue] = useState('');
  const [isOpenFilters, setIsOpenFilters] = useState(false);
  useResetSearchParams();

  const {
    queryObject: { page, rowsPerPage, filters = {} },
    changeFieldInURL,
    onApplySearchParams,
  } = useQueryParams();

  const { onSort, onSortTable, onResetSortField, getListItemSortMethod, getCellSortMethod } =
    useTableSort({
      filters,
      onApplySearchParams,
      page,
      rowsPerPage,
      searchValue,
      defaultFilters: defaultScriptExecutionFilters,
    });

  const onCellSort = useCallback(
    ({ field, sortMethod, sortingCellField, sortFieldName }) => {
      const reversedSortOrder =
        sortMethod === sortingMethods.initial ? sortingMethods.desc : reversedSortMap[sortMethod];

      onSort(field, reversedSortOrder, sortingCellField, sortFieldName);
    },
    [onSort],
  );

  const onApplyFilters = useCallback(
    filters => {
      onApplySearchParams({ filters, page: 1, limit: rowsPerPage, searchValue });
    },
    [onApplySearchParams, rowsPerPage, searchValue],
  );

  const onCloseFilters = useCallback(() => {
    setIsOpenFilters(false);
  }, [setIsOpenFilters]);

  const onOpenFilters = useCallback(() => {
    setIsOpenFilters(true);
  }, [setIsOpenFilters]);

  const debounceQueryHandler = useDebounceHandler((key, value) => {
    changeFieldInURL([{ key, value }]);
  }, 500);

  const onChangeSearchValue = useCallback(
    event => {
      setSearchValue(event.target.value);
      debounceQueryHandler('search', event.target.value.trim());
    },
    [debounceQueryHandler],
  );

  const onResetSearch = useCallback(() => {
    setSearchValue('');
    changeFieldInURL([defaultSearchParams]);
  }, [setSearchValue, changeFieldInURL]);

  const hasFilters = useMemo(() => checkHasFilters(filters), [filters]);

  const filteredCount = useMemo(() => {
    return !isLoading && (hasFilters || searchValue) ? totalCount : null;
  }, [totalCount, hasFilters, isLoading, searchValue]);

  const renderCustomCell = useCallback((column, row) => {
    if (column.name === 'userWriteable') {
      return (
        <TableCell key="status" onClick={e => e.stopPropagation()}>
          <Stack
            sx={{
              fontSize: '0.75rem',
              fontWeight: 400,
              color: '#DDDDDD',
            }}>
            {row.userWriteable ? 'Yes' : 'No'}
          </Stack>
        </TableCell>
      );
    }
    return (
      <GenericTableCell
        key={column?.name || column}
        isNumber={column?.isNumber}
        value={
          column?.shouldHideValue
            ? ''
            : isDateString(row[column?.name || column])
              ? formatDate(row[column?.name || column])
              : row[column?.name || column]
        }
        styles={column?.styles || {}}
        {...column?.props}
      />
    );
  }, []);

  return (
    <Box sx={{ position: 'relative' }}>
      <TableSearch
        onResetSearch={onResetSearch}
        onChangeSearchValue={onChangeSearchValue}
        searchValue={searchValue}
      />

      <FilterCount filteredCount={filteredCount} onOpenFilters={onOpenFilters} />

      <Paper
        elevation={0}
        sx={{
          display: 'flex',
          flexDirection: 'column',
        }}>
        <TableContainer
          square
          component={Paper}
          elevation={0}
          styles={{
            maxHeight: 'calc(100vh - 354px)',
          }}>
          <Table stickyHeader aria-label="collapsible table">
            <TableHeading
              headings={
                type === 'device'
                  ? scriptExecutionDeviceTableHeadings
                  : scriptExecutionTableHeadings
              }
              listCellStyles={{
                top: '0px',
                padding: '10px 16px',
              }}
              onSort={onSort}
              filterTopPisition="0"
              onSortTable={onSortTable}
              onResetSortField={onResetSortField}
              getListItemSortMethod={getListItemSortMethod}
              getCellSortMethod={getCellSortMethod}
              onCellSort={onCellSort}
            />

            <TableBody sx={{ backgroundColor: '#4B4B4B' }}>
              {isLoading ? (
                <LoadingRows columnCount={5} rowCount={3} />
              ) : (
                <>
                  {scriptExecutionList?.map((row, index) => {
                    return (
                      <Row
                        withTwoColors={true}
                        hasActions={false}
                        ref={index === scriptExecutionList.length - 1 ? lastElementRef : null}
                        key={row?.id}
                        row={row}
                        columnData={
                          type === 'device'
                            ? scriptExecutionDeviceTableColumnsOrder
                            : scriptExecutionTableColumnsOrder
                        }
                        isClickable={true}
                        source={ROW_SOURCES.scriptExecution}
                        renderCustomCell={renderCustomCell}
                      />
                    );
                  })}
                  {!isLoading && isFetching && (
                    <LoadingRows columnCount={scriptExecutionTableHeadings.length} rowCount={3} />
                  )}
                  {!isLoading && !isFetching && !scriptExecutionList?.length && (
                    <NoDataCell colSpan={scriptExecutionTableHeadings.length}>No Data</NoDataCell>
                  )}
                </>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <WdacScriptExecutionSidebar
          query={query}
          open={isOpenFilters}
          onClose={onCloseFilters}
          onApplyFilters={onApplyFilters}
          initialQueryFilters={filters}
        />
      </Paper>
    </Box>
  );
};

export default memo(WdacScriptExecutionTable);
